import React, { useState, useContext, useEffect } from 'react'
import { Modal, Button } from 'antd'

import { calculateRechargeType, shouldShowQ3 } from '../../util/recharge-type-calculator'
import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import { useAppSelector } from '../../store/hooks'
import { selectUserIdSlice } from '../../store/user-id-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { rechargeTypeTracking } from './services/rechargeTypeTracking'

import { QuizAnswers, RechargeType } from '@vacationtracker/shared/types/recharge-type'
import IntlMessages from '../../util/IntlMessages'
import './styles.less'

interface RechargeTypeQuizProps {
  visible: boolean
  onComplete: (rechargeType: RechargeType, answers: QuizAnswers) => void
  onCancel: () => void
  source?: string // Optional source for analytics (defaults to 'app/leaves/request-leave')
}

export const RechargeTypeQuiz: React.FC<RechargeTypeQuizProps> = ({
  visible,
  onComplete,
  onCancel,
  source = 'app/leaves/request-leave', // Default source
}) => {
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME
  const { userId } = useAppSelector(selectUserIdSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({})
  const [currentQuestion, setCurrentQuestion] = useState<1 | 2 | 3>(1)
  const [selectedOption, setSelectedOption] = useState<boolean | null>(null)

  // Track quiz started when modal opens
  useEffect(() => {
    if (visible && userId && authCompany?.id) {
      rechargeTypeTracking.trackQuizStarted({
        userId,
        companyId: authCompany.id,
        source,
      })
    }
  }, [visible, userId, authCompany?.id, source])

  // Reset state when modal closes (visible becomes false)
  useEffect(() => {
    if (!visible) {
      // Delay reset to allow modal close animation
      const timer = setTimeout(() => {
        setAnswers({})
        setCurrentQuestion(1)
        setSelectedOption(null)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [visible])

  const handleOptionSelect = (value: boolean) => {
    setSelectedOption(value)
  }

  const handleNext = () => {
    if (selectedOption === null) return

    // Track question answered
    if (userId && authCompany?.id) {
      rechargeTypeTracking.trackQuizQuestionAnswered({
        userId,
        companyId: authCompany.id,
        source,
        questionNumber: currentQuestion,
      })
    }

    const newAnswers = { ...answers, [`q${currentQuestion}`]: selectedOption }
    setAnswers(newAnswers)

    if (currentQuestion === 1) {
      setCurrentQuestion(2)
      setSelectedOption(null)
    } else if (currentQuestion === 2) {
      const fullAnswers = { q1: answers.q1!, q2: selectedOption }
      if (shouldShowQ3(fullAnswers)) {
        setCurrentQuestion(3)
        setSelectedOption(null)
      } else {
        const finalAnswers = { q1: answers.q1!, q2: selectedOption }
        const type = calculateRechargeType(finalAnswers)
        // Track completion
        if (userId && authCompany?.id) {
          rechargeTypeTracking.trackQuizCompleted({
            userId,
            companyId: authCompany.id,
            source,
            rechargeType: type,
          })
        }
        // Don't reset here - let the useEffect handle it after modal closes
        onComplete(type, finalAnswers)
      }
    } else if (currentQuestion === 3) {
      const finalAnswers = { q1: answers.q1!, q2: answers.q2!, q3: selectedOption }
      const type = calculateRechargeType(finalAnswers)
      // Track completion
      if (userId && authCompany?.id) {
        rechargeTypeTracking.trackQuizCompleted({
          userId,
          companyId: authCompany.id,
          source,
          rechargeType: type,
        })
      }
      // Don't reset here - let the useEffect handle it after modal closes
      onComplete(type, finalAnswers)
    }
  }

  const handleBack = () => {
    if (currentQuestion === 2) {
      setCurrentQuestion(1)
      setSelectedOption(answers.q1 ?? null)
    } else if (currentQuestion === 3) {
      setCurrentQuestion(2)
      setSelectedOption(answers.q2 ?? null)
    }
  }

  const handleCancel = () => {
    // Track dismissed
    if (userId && authCompany?.id) {
      rechargeTypeTracking.trackQuizModalDismissed({
        userId,
        companyId: authCompany.id,
        source,
        dismissedAt: currentQuestion === 1 ? 'recharge_q1' : currentQuestion === 2 ? 'recharge_q2' : 'recharge_q3',
      })
    }
    onCancel()
  }

  const renderQuestion = () => {
    const questions = {
      1: {
        title: <IntlMessages id="rechargeType.quiz.q1.newTitle" />,
        optionA: {
          title: <IntlMessages id="rechargeType.quiz.q1.optionA.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q1.optionA.desc" />,
          value: true,
        },
        optionB: {
          title: <IntlMessages id="rechargeType.quiz.q1.optionB.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q1.optionB.desc" />,
          value: false,
        },
      },
      2: {
        title: <IntlMessages id="rechargeType.quiz.q2.newTitle" />,
        optionA: {
          title: <IntlMessages id="rechargeType.quiz.q2.optionA.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q2.optionA.desc" />,
          value: false,
        },
        optionB: {
          title: <IntlMessages id="rechargeType.quiz.q2.optionB.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q2.optionB.desc" />,
          value: true,
        },
      },
      3: {
        title: <IntlMessages id="rechargeType.quiz.q3.newTitle" />,
        optionA: {
          title: <IntlMessages id="rechargeType.quiz.q3.optionA.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q3.optionA.desc" />,
          value: true,
        },
        optionB: {
          title: <IntlMessages id="rechargeType.quiz.q3.optionB.title" />,
          desc: <IntlMessages id="rechargeType.quiz.q3.optionB.desc" />,
          value: false,
        },
      },
    }

    const q = questions[currentQuestion]

    return (
      <div className="quiz-content">
        <h2 className={`quiz-question ${isDark ? 'dark' : ''}`}>{q.title}</h2>

        <div className="quiz-options">
          <div className={`quiz-option-card ${selectedOption === q.optionA.value ? 'selected' : ''} ${isDark ? 'dark' : ''}`} onClick={() => handleOptionSelect(q.optionA.value)}>
            <div className="option-label">A)</div>
            <div className="option-content">
              <div className="option-title">{q.optionA.title}</div>
              <div className="option-desc">{q.optionA.desc}</div>
            </div>
          </div>

          <div className={`quiz-option-card ${selectedOption === q.optionB.value ? 'selected' : ''} ${isDark ? 'dark' : ''}`} onClick={() => handleOptionSelect(q.optionB.value)}>
            <div className="option-label">B)</div>
            <div className="option-content">
              <div className="option-title">{q.optionB.title}</div>
              <div className="option-desc">{q.optionB.desc}</div>
            </div>
          </div>
        </div>

        <div className="quiz-actions">
          {currentQuestion > 1 && (
            <Button size="large" onClick={handleBack} className="quiz-back-btn">
              <IntlMessages id="app.back" />
            </Button>
          )}
          <Button type="primary" size="large" onClick={handleNext} disabled={selectedOption === null} className="quiz-next-btn">
            <IntlMessages id="app.next" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Modal open={visible} onCancel={handleCancel} footer={null} width={560} centered className="recharge-quiz-modal" closable={true}>
      {renderQuestion()}
    </Modal>
  )
}
